//Componentes generales
import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import SelectList from "../SelectList/SelectList.js";

import moment from "moment";

//Permisos
import {
  MODIFICA_ESTADO_COMPROBANTE,
  MODIFICA_COMPROBANTE_ESTADO_RECHAZADO,
  MODIFICA_GRUPOS_COMPROBANTES,
  REENVIO_SOLIMENO,
  VER_SELECT_DISTRIBUCION,
} from "../../utils/permisos";
import { ADMIN, PROV, CONTROL } from "../../utils/roles";

//Debe coincidir con el alto definido para .dropdown-pdf-preview en ReqList.css, se usa para decidir
//    si el recuadro de vista previa se despliega hacia abajo o hacia arriba según el espacio disponible.
const PDF_PREVIEW_HEIGHT = 600;
const PDF_PREVIEW_GAP = 4;

const RenderRow = ({
  usuario,
  index,
  style,
  listTDoc,
  filter,
  checkPermisos,
  cambiarEstado,
  editar,
  verDetalles,
  eliminar,
  statesNEviables,
  sendToAPI,
  disabledElements,
  cambiarGrupos,
  globalSettings,
  descargarPDF,
  obtenerAdicionales,
  obtenerArchivoPrincipal,
  distribucionesActivas,
  dimBuque,
  dimPlanta,
  cambiarDistribucion,
}) => {
  const [currentLista, setCurrentLista] = useState(listTDoc ? listTDoc : []);
  const [disabledElementsReq, setDisabledElementsReq] =
    useState(disabledElements);
  const [disabledButtonEnviar, setDisabledButtonEnviar] = useState(false);
  const [adicionalesAbierto, setAdicionalesAbierto] = useState(null);
  const [adicionalesCache, setAdicionalesCache] = useState({});
  const [adicionalesPos, setAdicionalesPos] = useState(null);
  const adicionalesBtnRef = useRef(null);
  const adicionalesCloseTimeoutRef = useRef(null);
  const [pdfPreviewAbierto, setPdfPreviewAbierto] = useState(null);
  const [pdfPreviewCache, setPdfPreviewCache] = useState({});
  const [pdfPreviewPos, setPdfPreviewPos] = useState(null);
  const pdfPreviewBtnRef = useRef(null);
  const pdfPreviewCloseTimeoutRef = useRef(null);

  //La vista de tarjeta "clásica" (Editar/Detalles/Modificar Estado/Modificar Grupos) se mantiene solo para usuarios ADMIN.
  const isAdmin = usuario.userType.some((item) => item.typeName === ADMIN);
  const isUserTypeProv = usuario.userType.some((item) => item.typeName === PROV);

  const puedeVerEliminar = globalSettings?.puedeEliminarPendientes === true && (
    usuario.userType.some((item) => [ADMIN, CONTROL].includes(item.typeName)) ||
    (globalSettings?.eliminarPendientesAplicaProv === true &&
      usuario.userType.some((item) => item.typeName === PROV))
  );

  useEffect(() => {
    setCurrentLista(listTDoc ? listTDoc : []);
  }, [listTDoc]);

  useEffect(() => {
    setDisabledElementsReq(disabledElements);
  }, [disabledElements]);

  /*
        Manejador que despliega el listado de documentación adicional de un comprobante al pasar el cursor,
            recuperando los archivos (alojados en S3) la primera vez que se despliega.
    */
  const handleMouseEnterAdicionales = async (tdoc) => {
    if (adicionalesCloseTimeoutRef.current) {
      clearTimeout(adicionalesCloseTimeoutRef.current);
      adicionalesCloseTimeoutRef.current = null;
    }
    if (adicionalesBtnRef.current) {
      const rect = adicionalesBtnRef.current.getBoundingClientRect();
      setAdicionalesPos({ top: rect.bottom, left: rect.left });
    }
    setAdicionalesAbierto(tdoc._id);
    if (!adicionalesCache[tdoc._id]) {
      setAdicionalesCache((prev) => ({ ...prev, [tdoc._id]: "loading" }));
      const docs = await obtenerAdicionales(tdoc);
      setAdicionalesCache((prev) => ({ ...prev, [tdoc._id]: docs || [] }));
    }
  };

  /*
        Manejador que contrae el listado de documentación adicional al sacar el cursor del botón o del contenido
            desplegado. Se demora un poco el cierre (y se cancela si el cursor vuelve a entrar) ya que, al estar el
            desplegable en un portal, dejar de pasar el cursor por el botón dispara este evento aunque el cursor
            se esté moviendo hacia el desplegable.
    */
  const handleMouseLeaveAdicionales = (tdoc) => {
    adicionalesCloseTimeoutRef.current = setTimeout(() => {
      setAdicionalesAbierto((actual) => (actual === tdoc._id ? null : actual));
    }, 150);
  };

  /*
        Manejador que despliega la vista previa del archivo principal (factura) de un comprobante al pasar el
            cursor por el botón PDF/JPG, recuperando el archivo (alojado en S3) la primera vez que se despliega.
    */
  const handleMouseEnterPdfPreview = async (tdoc) => {
    if (pdfPreviewCloseTimeoutRef.current) {
      clearTimeout(pdfPreviewCloseTimeoutRef.current);
      pdfPreviewCloseTimeoutRef.current = null;
    }
    if (pdfPreviewBtnRef.current) {
      const rect = pdfPreviewBtnRef.current.getBoundingClientRect();
      const espacioAbajo = window.innerHeight - rect.bottom;
      const abreHaciaArriba =
        espacioAbajo < PDF_PREVIEW_HEIGHT + PDF_PREVIEW_GAP &&
        rect.top > espacioAbajo;
      setPdfPreviewPos({
        top: abreHaciaArriba
          ? Math.max(PDF_PREVIEW_GAP, rect.top - PDF_PREVIEW_HEIGHT - PDF_PREVIEW_GAP)
          : rect.bottom + PDF_PREVIEW_GAP,
        left: rect.left,
      });
    }
    setPdfPreviewAbierto(tdoc._id);
    if (!pdfPreviewCache[tdoc._id]) {
      setPdfPreviewCache((prev) => ({ ...prev, [tdoc._id]: "loading" }));
      const files = await obtenerArchivoPrincipal(tdoc);
      const archivo = files && files.length > 0 ? files[0] : null;
      if (!archivo) {
        setPdfPreviewCache((prev) => ({ ...prev, [tdoc._id]: null }));
        return;
      }
      // previewUrl viene del backend con content-type/content-disposition "inline" forzados (ver
      //    getFileURLPreview en s3.ts), para que el navegador lo muestre en vez de descargarlo.
      const esImagen = /\.(jpe?g|png|gif|bmp|webp)$/i.test(archivo.name || "");
      setPdfPreviewCache((prev) => ({
        ...prev,
        [tdoc._id]: { previewUrl: archivo.previewUrl || archivo.url, esImagen },
      }));
    }
  };

  /*
        Manejador que contrae la vista previa del archivo principal al sacar el cursor del botón o del
            recuadro desplegado (mismo esquema de demora/cancelación que renderAdicionales).
    */
  const handleMouseLeavePdfPreview = (tdoc) => {
    pdfPreviewCloseTimeoutRef.current = setTimeout(() => {
      setPdfPreviewAbierto((actual) => (actual === tdoc._id ? null : actual));
    }, 150);
  };

  /*
        Construye las opciones del select de una dimensión (Buque/Planta) para un comprobante,
            filtrando por la empresa receptora del comprobante (mismo criterio que en FormReq).
    */
  const buildOpcionesDistribucion = (tdoc, dim) => {
    if (!dim) return [];
    return (distribucionesActivas || [])
      .filter(
        (d) =>
          d.codDim === dim.codDim &&
          (tdoc?.empresaReceptora?.companyName
            ? d.companyName === tdoc.empresaReceptora.companyName
            : true)
      )
      .map((d) => ({
        value: `${d.codDis}`,
        label: `${d.codDis} (${d.codDisDescrp})`,
      }));
  };

  /*
        Renderiza el botón y el listado desplegable de documentación adicional de un comprobante.
            Muestra los links de los archivos alojados en S3 con sus nombres (mismo comportamiento que en ReqInfo).
    */
  const renderAdicionales = (tdoc) => {
    const docs = adicionalesCache[tdoc._id];
    const abierto = adicionalesAbierto === tdoc._id;
    return (
      <div
        className="container-btn-docs-adicionales"
        ref={adicionalesBtnRef}
        onMouseEnter={() => handleMouseEnterAdicionales(tdoc)}
        onMouseLeave={() => handleMouseLeaveAdicionales(tdoc)}
      >
        <div className="btn-pdf-descarga btn-docs-adicionales">
          Documentación adicional
        </div>
        {abierto &&
          adicionalesPos &&
          ReactDOM.createPortal(
            <div
              className="dropdown-docs-adicionales"
              style={{
                position: "fixed",
                top: adicionalesPos.top,
                left: adicionalesPos.left,
              }}
              onMouseEnter={() => handleMouseEnterAdicionales(tdoc)}
              onMouseLeave={() => handleMouseLeaveAdicionales(tdoc)}
            >
              {docs === "loading" && (
                <div className="dropdown-docs-adicionales-empty">Cargando...</div>
              )}
              {docs !== "loading" && (!docs || docs.length === 0) && (
                <div className="dropdown-docs-adicionales-empty">
                  Sin documentación adicional
                </div>
              )}
              {docs !== "loading" && docs && docs.length > 0 && (
                <ul className="dropdown-docs-adicionales-list">
                  {docs.map((file) => (
                    <li key={file.name} onClick={() => window.open(file.url)}>
                      {file.name}
                    </li>
                  ))}
                </ul>
              )}
            </div>,
            document.body
          )}
      </div>
    );
  };

  /*
        Renderiza el botón PDF/JPG y el recuadro de vista previa del archivo principal de un comprobante
            (solo ADMIN). Al pasar el cursor se muestra la vista previa (imagen o PDF embebido); al hacer
            click se descarga/abre el archivo, mismo comportamiento que el botón PDF/JPG de CONTROL/PROV.
    */
  const renderPdfPreview = (tdoc) => {
    const preview = pdfPreviewCache[tdoc._id];
    const abierto = pdfPreviewAbierto === tdoc._id;
    return (
      <div
        className="container-btn-docs-adicionales"
        ref={pdfPreviewBtnRef}
        onMouseEnter={() => handleMouseEnterPdfPreview(tdoc)}
        onMouseLeave={() => handleMouseLeavePdfPreview(tdoc)}
      >
        <div
          className="btn-pdf-descarga"
          onClick={() => descargarPDF(tdoc)}
        >
          PDF/JPG
        </div>
        {abierto &&
          pdfPreviewPos &&
          ReactDOM.createPortal(
            <div
              className="dropdown-pdf-preview"
              style={{
                position: "fixed",
                top: pdfPreviewPos.top,
                left: pdfPreviewPos.left,
              }}
              onMouseEnter={() => handleMouseEnterPdfPreview(tdoc)}
              onMouseLeave={() => handleMouseLeavePdfPreview(tdoc)}
            >
              {preview === "loading" && (
                <div className="dropdown-docs-adicionales-empty">Cargando...</div>
              )}
              {preview !== "loading" && !preview && (
                <div className="dropdown-docs-adicionales-empty">
                  Sin archivo
                </div>
              )}
              {preview && preview !== "loading" && preview.esImagen && (
                <img
                  className="pdf-preview-image"
                  src={preview.previewUrl}
                  alt="Vista previa"
                />
              )}
              {preview && preview !== "loading" && !preview.esImagen && (
                <iframe
                  className="pdf-preview-iframe"
                  src={preview.previewUrl}
                  title="Vista previa"
                />
              )}
            </div>,
            document.body
          )}
      </div>
    );
  };

  /*
        Obtiene la opción actualmente seleccionada de una dimensión (Buque/Planta) para un comprobante.
    */
  const getDistribucionActual = (tdoc, dim) => {
    if (!dim) return { label: "Seleccione...", value: "" };
    const actual = (tdoc.distributions || []).find(
      (d) => d.codDim === dim.codDim
    );
    return actual
      ? { label: `${actual.codDis} (${actual.codDisDescrp})`, value: `${actual.codDis}` }
      : { label: `Seleccione ${dim.codDimDescrp}...`, value: "" };
  };

  /*
        Renderiza la tarjeta "clásica" del comprobante (solo ADMIN): tDocCodFor - tDocNroFor, codOri y state en el
            encabezado, campos Alta/Emisión/Importe/Receptora/Emitido por/Grupos, y los botones Editar/Detalles/
            Modificar Estado/Modificar Grupos/Eliminar.
    */
  const renderTarjetaClasica = (tdoc) => (
    <div className="container-r-list-item">
      <div className="container-r-list-item-header">
        <div className="r-list-item-header-clave">
          {tdoc.tDocCodFor + " - " + tdoc.tDocNroFor}
        </div>
        {renderPdfPreview(tdoc)}
        {tdoc.tDocNroForERP && (
          <span className="header-clave-info-erp">
            Nro ERP: {tdoc.tDocNroForERP}
          </span>
        )}
        <div className="header-clave-nombre" title={tdoc.nroCtaDescrp}>
          {tdoc.nroCtaDescrp}
        </div>
        <div className="container-codOri-observada">
          <div className="r-list-item-header-title">{tdoc.codOri}</div>
          {tdoc.observada && (
            <div className="r-list-item-details-observada">OBSERVADA</div>
          )}
        </div>
        <div className="r-list-item-details-clave">{tdoc.state}</div>
      </div>
      <div>
        <div className="container-r-list-item-details">
          <div className="r-list-item-details-title">
            Alta: {moment(tdoc.fecAlt).format("DD-MM-YYYY")}
          </div>
          <div className="r-list-item-details-title">
            Emisión: {moment(tdoc.dateOfIssue).format("DD-MM-YYYY")}
          </div>
          <div className="r-list-item-details-title">
            Importe: $ {tdoc.importeString}
          </div>
          {(tdoc.empresaReceptora?.razonSocial ||
            tdoc.empresaReceptora?.cuit) && (
            <div className="r-list-item-details-title">
              Receptora:{" "}
              {tdoc.empresaReceptora.razonSocial || tdoc.empresaReceptora.cuit}
            </div>
          )}
          <div className="r-list-item-details-title">
            Emitido por:{" "}
            {tdoc.user[0].fullName.length > 25
              ? tdoc.user[0].fullName.slice(0, 22) + "..."
              : tdoc.user[0].fullName}
          </div>
          <div className="r-list-item-details-title">
            Grupos: {tdoc.gruposV.map((grupo) => grupo.name).join(" | ")}
          </div>
        </div>
      </div>
      <div></div>
      <div className="container-r-list-item-btn">
        {(usuario.userType.some(
          (item) =>
            MODIFICA_ESTADO_COMPROBANTE.includes(item.typeName) &&
            tdoc.state == "PENDIENTE"
        ) || (
          tdoc.state == "RECHAZADO" &&
          usuario.userType.some((item) =>
            MODIFICA_COMPROBANTE_ESTADO_RECHAZADO.includes(item.typeName)
          )
        )) && (
          <div
            className={`r-list-item-btn-A ${
              disabledElementsReq ? "navbar-container-disabled" : ""
            }`}
            onClick={() => (!disabledElements ? cambiarEstado(tdoc) : null)}
          >
            Modificar Estado
          </div>
        )}
        {tdoc.state == "PENDIENTE" && (
          <div
            className={`${"r-list-item-btn-A"} ${
              disabledElementsReq ? "navbar-container-disabled" : ""
            }`}
            onClick={() => (!disabledElements ? editar(tdoc) : null)}
          >
            Editar
          </div>
        )}
        {tdoc.state == "PENDIENTE" && puedeVerEliminar && (
          <div
            className={`r-list-item-btn-A r-list-item-btn-delete ${
              disabledElementsReq ? "navbar-container-disabled" : ""
            }`}
            onClick={() => (!disabledElements ? eliminar(tdoc) : null)}
          >
            Eliminar
          </div>
        )}
        <div
          className={`${"r-list-item-btn-A"} ${
            disabledElementsReq ? "navbar-container-disabled" : ""
          }`}
          onClick={() => (!disabledElements ? verDetalles(tdoc) : null)}
        >
          Detalles
        </div>
        {checkPermisos(MODIFICA_GRUPOS_COMPROBANTES) && (
          <div
            className={`r-list-item-btn-A ${
              disabledElementsReq ? "navbar-container-disabled" : ""
            }`}
            onClick={() => (!disabledElements ? cambiarGrupos(tdoc) : null)}
          >
            Modificar Grupos
          </div>
        )}
      </div>
      {checkPermisos(REENVIO_SOLIMENO) &&
        !statesNEviables.includes(tdoc.state) &&
        tdoc.enviado === false && (
          <div
            className={`${"container-resend"} ${
              disabledElementsReq || disabledButtonEnviar
                ? "navbar-container-disabled"
                : ""
            }`}
            onClick={async () => {
              setDisabledButtonEnviar(true);
              if (!disabledElements || !disabledButtonEnviar) {
                await sendToAPI(tdoc);
              }
              setDisabledButtonEnviar(false);
            }}
          >
            {"Enviar >>>"}
          </div>
        )}
    </div>
  );

  /*
        Renderiza la tarjeta "nueva" del comprobante (no ADMIN): nombre del proveedor y codOri en el encabezado,
            botones PDF/JPG y Documentación adicional, selects de distribución Buque/Planta y Modificar Estado.
    */
  const renderTarjetaProveedor = (tdoc) => (
    <div className="container-r-list-item">
      <div className="container-r-list-item-header">
        <div className="r-list-item-header-clave-proveedor">
          {!isUserTypeProv && (
            <span className="header-clave-nombre" title={tdoc.nroCtaDescrp}>
              {tdoc.nroCtaDescrp}
            </span>
          )}
          <span className="header-clave-codOri">{tdoc.codOri}</span>
          {renderPdfPreview(tdoc)}
          {renderAdicionales(tdoc)}
          {isUserTypeProv && (
            <div className="header-clave-info-extra-stack">
              <div className="header-clave-info-extra-row">
                <span className="header-clave-info-extra">
                  Importe: $ {tdoc.importeString}
                </span>
                <span className="header-clave-info-extra" style={{marginLeft:"20px"}}>
                  Emisión: {moment(tdoc.dateOfIssue).format("DD-MM-YYYY")}
                </span>
              </div>
              {(tdoc.empresaReceptora?.razonSocial ||
                tdoc.empresaReceptora?.cuit) && (
                <span className="header-clave-info-extra header-clave-info-extra-receptora">
                  Empresa Receptora:{" "}
                  {tdoc.empresaReceptora.razonSocial ||
                    tdoc.empresaReceptora.cuit}
                </span>
              )}
            </div>
          )}
          {dimBuque && checkPermisos(VER_SELECT_DISTRIBUCION) && (
            <div className="select-distribucion-wrapper">
              <SelectList
                options={buildOpcionesDistribucion(tdoc, dimBuque)}
                handleChange={(value) =>
                  cambiarDistribucion(tdoc, dimBuque, value)
                }
                property={`distribucion-buque-${tdoc._id}`}
                valueAux={getDistribucionActual(tdoc, dimBuque)}
                text={"Buque"}
                disabled={tdoc.state !== "PENDIENTE"}
                borderColor="#888"
                styles={{
                  additionalStyleDivSelectList: {},
                  fontStyleTitle: {
                    width: "100%",
                    textAlign: "center",
                    fontWeight: "bold",
                  },
                }}
                menuPortal={true}
                controlHeight={"32px"}
                maxHeightMenu={"300px"}
              />
            </div>
          )}
          {dimPlanta && checkPermisos(VER_SELECT_DISTRIBUCION) && (
            <div className="select-distribucion-wrapper">
              <SelectList
                options={buildOpcionesDistribucion(tdoc, dimPlanta)}
                handleChange={(value) =>
                  cambiarDistribucion(tdoc, dimPlanta, value)
                }
                property={`distribucion-planta-${tdoc._id}`}
                valueAux={getDistribucionActual(tdoc, dimPlanta)}
                text={"Planta"}
                disabled={tdoc.state !== "PENDIENTE"}
                borderColor="#888"
                styles={{
                  additionalStyleDivSelectList: {},
                  fontStyleTitle: {
                    width: "100%",
                    textAlign: "center",
                    fontWeight: "bold",
                  },
                }}
                menuPortal={true}
                controlHeight={"32px"}
                maxHeightMenu={"300px"}
              />
            </div>
          )}
        </div>
        <div className="container-codOri-observada">
          {tdoc.observada && (
            <div className="r-list-item-details-observada">OBSERVADA</div>
          )}
        </div>
        {(usuario.userType.some(
          (item) =>
            MODIFICA_ESTADO_COMPROBANTE.includes(item.typeName) &&
            tdoc.state == "PENDIENTE"
        ) || (
          tdoc.state == "RECHAZADO" &&
          usuario.userType.some((item) =>
            MODIFICA_COMPROBANTE_ESTADO_RECHAZADO.includes(item.typeName)
          )
        )) && (
          <div
            className={`r-list-item-header-btn-estado ${
              disabledElementsReq ? "navbar-container-disabled" : ""
            }`}
            onClick={() => (!disabledElements ? cambiarEstado(tdoc) : null)}
          >
            Modificar Estado
          </div>
        )}
      </div>
      {checkPermisos(REENVIO_SOLIMENO) &&
        !statesNEviables.includes(tdoc.state) &&
        tdoc.enviado === false && (
          <div
            className={`${"container-resend"} ${
              disabledElementsReq || disabledButtonEnviar
                ? "navbar-container-disabled"
                : ""
            }`}
            onClick={async () => {
              setDisabledButtonEnviar(true);
              if (!disabledElements || !disabledButtonEnviar) {
                await sendToAPI(tdoc);
              }
              setDisabledButtonEnviar(false);
            }}
          >
            {"Enviar >>>"}
          </div>
        )}
    </div>
  );

  if (currentLista && currentLista.length > 0) {
    const formatter = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    currentLista.map((tdoc) => {
      tdoc.importeString = formatter.format(tdoc.importe ? tdoc.importe : 0); //Se crea a modo visual la propiedad importeString.
    });
    if (filter === "") {
      const tdoc = currentLista[index];
      return (
        <div
          style={{
            ...style,
            zIndex:
              adicionalesAbierto === tdoc._id || pdfPreviewAbierto === tdoc._id
                ? 30
                : style.zIndex,
          }}
        >
          {isAdmin ? renderTarjetaClasica(tdoc) : renderTarjetaProveedor(tdoc)}
        </div>
      );
    } else {
      const req_filters = currentLista.filter(
        (r) =>
          r.tDocCodFor
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(filter.toLowerCase().replace(/\s+/g, "")) ||
          (
            r.tDocCodFor.toLowerCase() +
            "-" +
            r.tDocNroFor.toString().toLowerCase().replace(/\s+/g, "")
          ).includes(filter.toLowerCase().replace(/\s+/g, "")) ||
          r.tDocNroFor
            .toString()
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(filter.toLowerCase().replace(/\s+/g, "")) ||
          r.codOri
            .toString()
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(filter.toLowerCase().replace(/\s+/g, "")) ||
          (r.nroCtaDescrp || "")
            .toString()
            .toLowerCase()
            .replace(/\s+/g, "")
            .includes(filter.toLowerCase().replace(/\s+/g, ""))
      );
      const tdoc = req_filters[index];
      return (
        <div
          style={{
            ...style,
            zIndex:
              adicionalesAbierto === tdoc._id || pdfPreviewAbierto === tdoc._id
                ? 30
                : style.zIndex,
          }}
        >
          {isAdmin ? renderTarjetaClasica(tdoc) : renderTarjetaProveedor(tdoc)}
        </div>
      );
    }
  }
};

export default RenderRow;
